"use client";

import React from "react";
import { Phone, Mail, Clock, Loader, CircleCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center space-y-4 bg-muted/30 rounded-xl border border-border/50 animate-in fade-in zoom-in-95 duration-300">
        <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-green-600 dark:bg-green-900/30 dark:text-green-400">
          <CircleCheck className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif font-semibold text-xl tracking-tight">Message Sent</h3>
          <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-2">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Full Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Full Name</FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" {...field} />
            </FormControl>
            <FormDescription>Enter your first and last name.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" {...field} />
            </FormControl>
            <FormDescription>We'll use this to reply to your inquiry.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+61 4XX XXX XXX" {...field} />
              </FormControl>
              <FormDescription>Please provide either a email or phone number to be contacted.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="ndis_number" render={({
        field
      }) => <FormItem>
              <FormLabel>NDIS Participant Number (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="1234 5678 9012" {...field} />
              </FormControl>
              
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us how we can help you..." className="min-h-[120px]" {...field} />
            </FormControl>
            <FormDescription>Provide details about your needs or questions.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" className="w-full md:w-auto" disabled={isSubmitting}>
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Sending...
          </> : "Send Message"}
      </Button>
    </Form>;
}
export default function ConnectWithResolve() {
  return <section id="connect-with-resolve" className="relative bg-background py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none flex items-center justify-end opacity-[0.03] dark:opacity-[0.05]">
        <div className="w-full h-full max-w-3xl bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1556888/img/sa_map_watermark.png)] bg-contain bg-no-repeat bg-center md:bg-right translate-x-1/4" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          <div className="w-full">
            <div className="mb-8">
              <h2 className="text-3xl font-serif font-bold tracking-tight text-foreground sm:text-4xl mb-4">
                Connect with Resolve
              </h2>
              <p className="text-lg text-muted-foreground">
                Whether you are a participant, a plan manager, or a support coordinator, we are here to help you navigate the NDIS journey with clarity and confidence.
              </p>
            </div>

            <Card className="border-border/60 shadow-sm bg-card/50 backdrop-blur-sm">
              <CardContent className="p-6 sm:p-8">
                <FormProvider formId="connect_form_contact" sectionName="connect-with-resolve" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                ndis_number: "",
                message: ""
              }} validationRules={{
                full_name: {
                  required: true
                },
                message: {
                  required: true
                }
              }} submitText="Send Message" successMessage="Thank you for reaching out. A member of our team will review your inquiry and get back to you shortly.">
                  <ContactForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col gap-10 lg:pt-12">
            <div className="space-y-6">
              <h3 className="text-2xl font-serif font-semibold tracking-tight text-foreground">
                Contact Information
              </h3>
              <p className="text-muted-foreground">Based in Adelaide, we service participants across South Australia. Reach out directly via email or phone for enquiries.</p>
              <p className="text-muted-foreground text-sm italic">Please note: As an unregistered provider, we can only provide services to NDIS participants who are self-managed or plan-managed.</p>

              <div className="space-y-6 pt-2">
                <div className="flex items-start gap-4 group">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary shadow-sm transition-colors group-hover:border-primary/50 group-hover:text-primary">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium leading-none text-foreground">Email Us</p>
                    <Link to="mailto:info@resolveclinical.com.au" className="text-sm text-muted-foreground hover:text-primary transition-colors underline underline-offset-4">info@resolveclinicalservices.com.au</Link>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary shadow-sm transition-colors group-hover:border-primary/50 group-hover:text-primary">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium leading-none text-foreground">Phone</p>
                    <Link to="tel:+61478046866" className="text-sm text-muted-foreground hover:text-primary transition-colors">+61 0478 046 866</Link>
                  </div>
                </div>
              </div>
            </div>

            <Separator className="bg-border/60" />

            <div className="space-y-6">
              <h3 className="text-2xl font-serif font-semibold tracking-tight text-foreground">
                Office Hours
              </h3>
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-primary shadow-sm">
                  <Clock className="h-5 w-5" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium leading-none text-foreground">Standard Hours</p>
                  <div className="text-sm text-muted-foreground space-y-4">
                    <p>Monday to Friday: 9am - 5pm</p>
                    <p className="italic">We understand flexibility is important. After-hours appointments may be available by prior arrangement for existing participants.</p>
                    <div className="pt-2 border-t border-border/40">
                      <p className="font-medium text-foreground mb-1">Emergency Support</p>
                      <p>Resolve is not a crisis service. For urgent mental health support please call 000 or mental health triage on 13 14 65.</p>
                      <p className="mt-2">Phone‑based support is also available via Lifeline at 13 11 14.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}